import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';

export const courseApi = createApi({
    reducerPath: 'courseApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '/api/v1/',
        credentials: 'include',
        prepareHeaders(headers) {
            const csrfToken = Cookies.get('csrftoken');
            if (csrfToken) {
                headers.set('X-CSRFToken', csrfToken);
            }
            return headers;
        },
    }),
    tagTypes: [
        'course-list',
        'lesson-list',
        'submission-list',
        'notification-list',
        'course-draft-list',
        'student-list',
        'user-list',
        'admin-course-list',
    ],
    endpoints: (builder) => ({
        getCourseList: builder.query({
            query: () => '/published-courses/',
            providesTags: ['course-list'],
        }),
        getCourseDetail: builder.query({
            query: (id) => `/published-courses/${id}/`,
            providesTags: (result, error, id) => [{ type: 'course-list', id }],
        }),
        getCourseStepsList: builder.query({
            query: (id) => `/courses/${id}/steps/`,
            providesTags: (result, error, id) => [{ type: 'course-list', id }],
        }),
        getCourseUserProgress: builder.query({
            query: (id) => `/courses/${id}/user-progress/`,
            providesTags: (result, error, id) => [{ type: 'course-list', id }],
        }),
        getComponentList: builder.query({
            query: () => '/lessons/',
            providesTags: ['lesson-list'],
        }),
        getComponentDetails: builder.query({
            query: (id) => `/lessons/${id}/`,
            providesTags: (result, error, id) => [{ type: 'lesson-list', id }],
        }),
        getCourseDraftsList: builder.query({
            query: () => '/course-drafts/',
            providesTags: ['course-draft-list'],
        }),
        getCourseDraftDetails: builder.query({
            query: (id) => `/course-drafts/${id}/`,
            providesTags: (result, error, id) => [
                { type: 'course-draft-list', id },
            ],
        }),
        getSubmissionInbox: builder.query({
            query: () => '/submission-inbox/',
            providesTags: ['submission-list'],
        }),
        getNotifications: builder.query({
            query: () => '/notifications/',
            providesTags: ['notification-list'],
        }),
        getStudentsList: builder.query({
            query: (courseId) =>
                courseId ? `/students/?course_id=${courseId}` : '/students/',
            providesTags: ['student-list'],
        }),
        getUsersList: builder.query({
            query: (role) => (role ? `/users/?role=${role}` : '/users/'),
            providesTags: ['user-list'],
        }),
        getAdminCourseList: builder.query({
            query: () => '/admin/courses/',
            providesTags: ['admin-course-list'],
        }),
        getMyCourseStats: builder.query({
            query: (courseId) => `/analytics/my-course-stats/${courseId}/`,
            providesTags: (result, error, id) => [{ type: 'course-list', id }],
        }),
        getClassStats: builder.query({
            query: () => '/analytics/class-stats/',
            providesTags: ['submission-list'],
        }),

        issueCommand: builder.mutation({
            query: ({ command }) => ({
                url: '/commands/',
                method: 'POST',
                body: command,
            }),
            invalidatesTags: [
                'course-list',
                'lesson-list',
                'submission-list',
                'course-draft-list',
                'notification-list',
                'student-list',
            ],
        }),
    }),
});

export const {
    useIssueCommandMutation,
    useGetCourseListQuery,
    useGetCourseDetailQuery,
    useGetCourseStepsListQuery,
    useGetCourseUserProgressQuery,
    useGetCourseDraftsListQuery,
    useGetCourseDraftDetailsQuery,
    useGetComponentListQuery,
    useGetComponentDetailsQuery,
    useGetSubmissionInboxQuery,
    useGetNotificationsQuery,
    useGetStudentsListQuery,
    useGetUsersListQuery,
    useGetAdminCourseListQuery,
    useGetMyCourseStatsQuery,
    useGetClassStatsQuery,
} = courseApi;
export default courseApi;
